import type {SketchRelationDefinition} from '../relationDefinition/SketchRelationDefinition';
import {isArcRadiusConstraint} from '../relationDefinition/circle/ArcRadiusConstraint';
import {isCircleDiameterConstraint} from '../relationDefinition/circle/CircleDiameterConstraint';
import {isLineAngleConstraint} from '../relationDefinition/line/LineAngleConstraint';
import {isPointDistanceConstraint} from '../relationDefinition/point/PointDistanceConstraint';
import {isThreePointAngleConstraint} from '../relationDefinition/point/ThreePointAngleConstraint';
import {SketchRelationKind} from './SketchRelationKinds';

/**
 * 关系类型定义（显示名与数值语义的单一事实源）。
 *
 * 设计说明：
 * 1. 一个关系类型的"名字 / 有没有数值"此前分散三处各写一份（显示名 switch、
 *    序列化的数值读取、反序列化的数值注入清单），漏登记的后果还不一致：
 *    显示名漏了只是显示英文枚举名，**数值漏了会静默丢数据**（重开文档退化为未锁定）；
 * 2. 这里集中声明、其余位置一律派生；数值访问器同时供序列化与反序列化使用，
 *    保证两侧永远同步，不会再出现"存得下读不回"；
 * 3. 分类（自身/多目标）刻意留在零依赖的 `SketchRelationKinds`：本表依赖约束类，
 *    若枚举模块反向依赖本表会形成循环导入，命中本仓库已知的
 *    "循环初始化期读到 undefined"陷阱（约束类映射必须惰性构建即同一原因）。
 */
export interface SketchRelationMetadata {
    /** 关系类型。 */
    readonly kind: SketchRelationKind;
    /** 中文显示名（属性栏/关系列表）。 */
    readonly displayName: string;
    /**
     * 数值访问器；非数值关系省略。
     *
     * 声明本字段即表示该关系的锁定值必须随文档存取：序列化经 `read` 读出，
     * 反序列化经约束构造函数第一参数注入。
     */
    readonly numeric?: {
        readonly read: (constraint: SketchRelationDefinition) => number | null;
    };
}

/** 关系类型定义表。 */
const SKETCH_RELATION_DEFINITIONS: readonly SketchRelationMetadata[] = [
    {kind: SketchRelationKind.Horizontal, displayName: '水平'},
    {kind: SketchRelationKind.Vertical, displayName: '竖直'},
    {kind: SketchRelationKind.Fix, displayName: '固定'},
    {kind: SketchRelationKind.FixSlot, displayName: '固定槽口'},
    {
        kind: SketchRelationKind.Radius,
        displayName: '半径',
        numeric: {read: (constraint) => (isArcRadiusConstraint(constraint) ? constraint.getLockedRadius() : null)},
    },
    {
        kind: SketchRelationKind.Diameter,
        displayName: '直径',
        numeric: {read: (constraint) => (isCircleDiameterConstraint(constraint) ? constraint.getLockedDiameter() : null)},
    },
    {
        kind: SketchRelationKind.Distance,
        displayName: '距离',
        numeric: {read: (constraint) => (isPointDistanceConstraint(constraint) ? constraint.getLockedDistance() : null)},
    },
    {
        kind: SketchRelationKind.Angle,
        displayName: '角度',
        numeric: {read: (constraint) => (isLineAngleConstraint(constraint) ? constraint.getLockedSignedAngle() : null)},
    },
    {kind: SketchRelationKind.Collinear, displayName: '共线'},
    {kind: SketchRelationKind.Coradial, displayName: '共圆心同半径'},
    {kind: SketchRelationKind.Perpendicular, displayName: '垂直'},
    {kind: SketchRelationKind.Parallel, displayName: '平行'},
    {kind: SketchRelationKind.ParallelToYZ, displayName: '与 YZ 平行'},
    {kind: SketchRelationKind.ParallelToZX, displayName: '与 ZX 平行'},
    {kind: SketchRelationKind.AlongZ, displayName: '沿 Z'},
    {kind: SketchRelationKind.Tangent, displayName: '相切'},
    {kind: SketchRelationKind.Concentric, displayName: '同心'},
    {kind: SketchRelationKind.Midpoint, displayName: '中点'},
    {kind: SketchRelationKind.Intersection, displayName: '交点'},
    {kind: SketchRelationKind.Coincident, displayName: '重合'},
    {kind: SketchRelationKind.Equal, displayName: '相等'},
    {kind: SketchRelationKind.EqualCurvature, displayName: '相等曲率'},
    {kind: SketchRelationKind.EqualCurveLength, displayName: '曲线长度相等'},
    {kind: SketchRelationKind.Symmetric, displayName: '对称'},
    {kind: SketchRelationKind.Pierce, displayName: '穿透'},
    {kind: SketchRelationKind.MergePoints, displayName: '合并点'},
    {kind: SketchRelationKind.TwiceDistance, displayName: '两倍距离'},
    {kind: SketchRelationKind.EqualSlot, displayName: '相等槽口'},
    {kind: SketchRelationKind.Patterned, displayName: '阵列'},
    {
        kind: SketchRelationKind.ThreePointAngle,
        displayName: '三点定角',
        numeric: {read: (constraint) => (isThreePointAngleConstraint(constraint) ? constraint.getLockedAngle() : null)},
    },
    {kind: SketchRelationKind.OnEdge, displayName: '在线上'},
    {kind: SketchRelationKind.OnPlane, displayName: '在平面上'},
    {kind: SketchRelationKind.OnSurface, displayName: '在曲面上'},
    {kind: SketchRelationKind.TangentToFace, displayName: '与面相切'},
    {kind: SketchRelationKind.TorsionContinuity, displayName: '扭转连续性'},
];

/** 关系类型 → 定义索引（惰性构建，规避 constraint 包既有循环依赖下的模块求值顺序问题）。 */
let definitionByKindCache: ReadonlyMap<SketchRelationKind, SketchRelationMetadata> | null = null;

/** 解析定义索引。 */
function resolveDefinitionIndex(): ReadonlyMap<SketchRelationKind, SketchRelationMetadata> {
    if (!definitionByKindCache) {
        definitionByKindCache = new Map(
            SKETCH_RELATION_DEFINITIONS.map((definition) => [definition.kind, definition]),
        );
    }
    return definitionByKindCache;
}

/**
 * 解析关系类型定义。
 *
 * @param kind 关系类型。
 * @returns 定义；未登记时返回 null。
 */
export function resolveSketchRelationMetadata(kind: SketchRelationKind): SketchRelationMetadata | null {
    return resolveDefinitionIndex().get(kind) ?? null;
}

/**
 * 解析关系类型显示名。
 *
 * @param kind 关系类型。
 * @returns 中文显示名；未登记时回退为枚举名。
 */
export function resolveSketchRelationDisplayName(kind: SketchRelationKind): string {
    return resolveSketchRelationMetadata(kind)?.displayName ?? String(kind);
}

/**
 * 判断关系是否携带需要持久化的数值。
 *
 * @param kind 关系类型。
 * @returns 是数值关系时返回 true。
 */
export function isNumericSketchRelationKind(kind: SketchRelationKind): boolean {
    return resolveSketchRelationMetadata(kind)?.numeric !== undefined;
}

/**
 * 读取数值关系的锁定值。
 *
 * @param kind 关系类型。
 * @param constraint 约束实例。
 * @returns 锁定值；非数值关系或未锁定时为 null。
 */
export function readSketchRelationNumericValue(
    kind: SketchRelationKind,
    constraint: SketchRelationDefinition,
): number | null {
    return resolveSketchRelationMetadata(kind)?.numeric?.read(constraint) ?? null;
}
